/**
 * Adapters that turn our typed records into MapLibre-ready GeoJSON
 * FeatureCollections.
 *
 * Each layer in App.tsx feeds a MapLibre source from one of these. We
 * keep properties narrow (only what map style expressions reference) so
 * cluster aggregation stays cheap and the JSON payload stays small.
 *
 * One exception, and it bites: heritage records have no in-memory index.
 * Both the click path (App.tsx setSelected from f.properties) and the
 * deep-link path read the feature back out, so toHeritageGeoJSON's literal
 * is the ONLY route from api/* to HeritageDetail. A field left out of it is
 * fetched, stored and silently never rendered — which is what happened to
 * protectionStatus/condition/access/nkah. geojson.test.ts guards the round
 * trip; add new HeritageSite fields here too.
 *
 * Pure functions — no I/O, no map references.
 */
import type { HeritageSite, Monument } from '../types/index.ts';
import { yearProps, yearsFromDating, normalisePeriod } from './yearRange.ts';

export function toHeritageGeoJSON(sites: HeritageSite[]): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: sites.map((s) => ({
      type: "Feature" as const,
      geometry: { type: "Point" as const, coordinates: [s.lng, s.lat] },
      properties: {
        id: s.id, name: s.name, type: s.type, period: normalisePeriod(s.period), country: s.country,
        // y0/y1: the register's own span where it states one (yearSpan), else
        // the period word resolved against the country's chronology, for the
        // timeline. Two scalars, absent when undated (utils/yearRange).
        ...(s.yearSpan ? { y0: s.yearSpan[0], y1: s.yearSpan[1] } : yearProps(s.period, s.country)),
        sourceUrl: s.sourceUrl, description: s.description ?? "", raaUuid: s.raaUuid ?? "", municipality: s.municipality ?? "",
        originalFunction: s.originalFunction ?? "", currentFunction: s.currentFunction ?? "",
        protectionDate: s.protectionDate ?? "", siteCount: s.siteCount ?? 0,
        layerType: "heritage", ...(s.sami ? { sami: true } : {}), ...(s.wikidataId ? { wikidataId: s.wikidataId } : {}),
        // Optional and spread rather than defaulted: absent fields cost no
        // payload, and HeritageDetail's meta rows already test for falsy.
        ...(s.protectionStatus ? { protectionStatus: s.protectionStatus } : {}),
        ...(s.valueCategories ? { valueCategories: s.valueCategories } : {}),
        ...(s.condition ? { condition: s.condition } : {}),
        ...(s.access ? { access: s.access } : {}),
        ...(s.nkah ? { nkah: s.nkah } : {}),
        ...(s.notVisible ? { notVisible: true } : {}),
        ...(s.altName ? { altName: s.altName } : {}),
        ...(s.wikipediaUrl ? { wikipediaUrl: s.wikipediaUrl } : {}),
        ...(s.periodBasis ? { periodBasis: s.periodBasis } : {}),
        ...(s.typeCy ? { typeCy: s.typeCy } : {}),
        ...(s.sourceUrlCy ? { sourceUrlCy: s.sourceUrlCy } : {}),
        ...(s.approximate ? { approximate: true } : {}),
        ...(s.riskLevel ? { riskLevel: s.riskLevel } : {}),
        ...(s.riskCause ? { riskCause: s.riskCause } : {}),
        // Split into two scalars rather than passed whole. MapLibre stringifies
        // anything non-scalar in a property bag, so `{iso, kind}` would reach
        // the panel as "[object Object]" — the same trap that made
        // valueCategories a joined string. sourceDateFromParts puts it back.
        ...(s.sourceDate ? { sourceDateIso: s.sourceDate.iso, sourceDateKind: s.sourceDate.kind } : {}),
      },
    })),
  };
}

function buildingYears(text: string | undefined): { y0: number; y1: number } | Record<string, never> {
  const r = yearsFromDating(text);
  return r ? { y0: r[0], y1: r[1] } : {};
}

export function toSimpleGeoJSON(items: Array<{ id: string; lng: number; lat: number; name: string; country?: string; type?: string; monumentType?: string; period?: string; dating?: string; constructionYear?: string; years?: [number, number] | null }>, layerType: string): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: items.map((item) => ({
      type: "Feature" as const,
      geometry: { type: "Point" as const, coordinates: [item.lng, item.lat] },
      properties: {
        id: item.id, name: item.name, layerType, country: item.country ?? "SE",
        ...(item.monumentType ? { monumentType: item.monumentType } : {}),
        ...(item.type ? { type: item.type } : {}),
        // Barrows, rock art, industrial sites and wrecks carry a period word
        // of their own; the timeline needs it and its years on the feature.
        ...(item.period ? { period: normalisePeriod(item.period), ...yearProps(item.period, item.country) } : {}),
        // A building's own year beats its period word: "1875-1899" is a
        // span the timeline can use, where "Historisk Tid" is four centuries.
        ...buildingYears(item.dating ?? item.constructionYear),
        // Layers dated from what they are (utils/yearRange::runestoneYears and
        // friends) hand the span in ready-made, and it beats the others.
        ...(item.years ? { y0: item.years[0], y1: item.years[1] } : {}),
      },
    })),
  };
}

export function deduplicateMonuments(monuments: Monument[], heritageFeatures: GeoJSON.Feature[]): Monument[] {
  // 4 decimals ≈ 11 m, tight enough to avoid merging neighbouring monuments
  // while still tolerant of small rounding differences across data sources.
  // (Previous 3-decimal precision merged sites within ~111 m of each other.)
  const heritageCoords = new Set<string>();
  heritageFeatures.forEach(f => {
    if (f.geometry.type === "Point") {
      const [lng, lat] = (f.geometry as GeoJSON.Point).coordinates;
      heritageCoords.add(`${lng.toFixed(4)},${lat.toFixed(4)}`);
    }
  });
  return monuments.filter(m => {
    const key = `${m.lng.toFixed(4)},${m.lat.toFixed(4)}`;
    return !heritageCoords.has(key);
  });
}
